import { useState } from "react";
import { useFetch } from "../../hooks/useFetch";
import { useCollapsible } from "../../hooks/useCollapsible";
import { getHackathons, getCertificates, getAchievements } from "../../services/firestore";
import { LoadingTerminal } from "../ui/LoadingState";
import "./EventSections.css";

const RESULT_TONES = {
  "1st place":   "gold",
  "2nd place":   "purple",
  "3rd place":   "green",
  "finalist":    "indigo",
  "top 10":      "indigo",
  "participant": "gray",
};

const COLLAPSE_COUNT = 4;

function PinIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function GitHubIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
    </svg>
  );
}

function ViewMoreButton({ expanded, toggle, total, initialCount }) {
  if (total <= initialCount) return null;
  return (
    <div className="event-viewmore">
      <button type="button" className="event-viewmore__btn" onClick={toggle}>
        {expanded ? "show less ↑" : `view all ${total} ↓`}
      </button>
    </div>
  );
}

// ── Hackathons ────────────────────────────────────────────────

function HackathonCard({ h }) {
  const tone = RESULT_TONES[h.result?.toLowerCase()] || "gray";

  return (
    <div className="event-card card">
      <div className="event-card__top">
        <span className={`event-card__result event-card__result--${tone}`}>
          {h.result}
        </span>
        {h.date && (
          <span className="event-card__date">
            {new Date(h.date).toLocaleDateString("en-IN", { year: "numeric", month: "short" })}
          </span>
        )}
      </div>
      <h3 className="event-card__title">{h.name}</h3>
      {h.organizer && <p className="event-card__sub">by {h.organizer}</p>}
      {!h.project_url && h.project_name && (
        <p className="event-card__project">
          <span className="event-card__label">Project:</span> {h.project_name}
        </p>
      )}
      {h.description && <p className="event-card__desc">{h.description}</p>}
      {h.project_url && (
        <a
          href={h.project_url}
          target="_blank"
          rel="noopener noreferrer"
          className="event-card__project-btn"
        >
          <GitHubIcon />
          {h.project_name || "View project"}
        </a>
      )}
      {h.location && (
        <div className="event-card__meta">
          <PinIcon /> {h.location}
        </div>
      )}
    </div>
  );
}

export function Hackathons() {
  const { data, loading } = useFetch(getHackathons, []);
  const items = data || [];

  return (
    <section id="hackathons" className="section event-section">
      <div className="container">
        <h2 className="section-title">
          <span className="section-title__index">04</span>
          Hackathons
        </h2>
        {loading ? (
          <LoadingTerminal
            title="hackathons.load"
            lines={[
              { variant: "headline" },
              { variant: "title" },
              { variant: "text" },
              { variant: "text", style: { width: "70%" } },
            ]}
            meta="loading hackathons..."
          />
        ) : items.length === 0 ? (
          <div className="event-empty card">
            <h3 className="event-empty__title">No hackathons yet</h3>
            <p className="event-empty__text">Add hackathon entries in the admin panel to show them here.</p>
          </div>
        ) : (
          <div className="grid-3">
            {items.map(h => <HackathonCard key={h.id} h={h} />)}
          </div>
        )}
      </div>
    </section>
  );
}


// ── Certificates ──────────────────────────────────────────────

function ExternalLinkIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function CertRow({ cert }) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = Boolean(cert.image_url) && !imgFailed;

  return (
    <li className="cert-row">
      {showImage && (
        <img
          className="cert-row__img"
          src={cert.image_url}
          alt={`${cert.issuer || "Certificate"} logo`}
          loading="lazy"
          onError={() => setImgFailed(true)}
        />
      )}
      {!showImage && (
        <span className="cert-row__badge" aria-hidden="true">
          {cert.issuer?.charAt(0)?.toUpperCase() || "C"}
        </span>
      )}
      <div className="cert-row__main">
        <h3 className="cert-row__title">{cert.title}</h3>
        <p className="cert-row__issuer">
          {cert.issuer}
          {cert.credential_id && <span className="cert-row__id"> · ID {cert.credential_id}</span>}
        </p>
      </div>
      {cert.issue_date && (
        <span className="cert-row__date">
          {new Date(cert.issue_date).toLocaleDateString("en-IN", { year: "numeric", month: "short" })}
        </span>
      )}
      {cert.credential_url && (
        <a
          href={cert.credential_url}
          target="_blank"
          rel="noopener noreferrer"
          className="cert-row__link"
          title="Verify credential"
          aria-label={`Verify ${cert.title}`}
        >
          <ExternalLinkIcon />
        </a>
      )}
    </li>
  );
}

export function Certificates() {
  const { data, loading } = useFetch(getCertificates, []);
  const items = data || [];
  const { visibleItems, expanded, toggle, hasMore } = useCollapsible(items, COLLAPSE_COUNT);

  return (
    <section id="certificates" className="section event-section">
      <div className="container">
        <h2 className="section-title">
          <span className="section-title__index">06</span>
          Certificates
        </h2>
        {loading ? (
          <LoadingTerminal
            title="certificates.load"
            lines={[
              { variant: "headline" },
              { variant: "title" },
              { variant: "text" },
              { variant: "text", style: { width: "65%" } },
            ]}
            meta="loading certificates..."
          />
        ) : items.length === 0 ? (
          <div className="event-empty card">
            <h3 className="event-empty__title">No certificates yet</h3>
            <p className="event-empty__text">Add certificates in the admin panel to show them here.</p>
          </div>
        ) : (
          <>
            <ul className="cert-list">
              {visibleItems.map(c => <CertRow key={c.id} cert={c} />)}
            </ul>
            <ViewMoreButton expanded={expanded} toggle={toggle} total={items.length} initialCount={COLLAPSE_COUNT} />
          </>
        )}
      </div>
    </section>
  );
}


// ── Achievements ──────────────────────────────────────────────

function AchievementRow({ a }) {
  return (
    <div className="achievement-row">
      <div className="achievement-row__icon">{a.icon || "🏆"}</div>
      <div className="achievement-row__content">
        <h3 className="achievement-row__title">{a.title}</h3>
        {a.description && <p className="achievement-row__desc">{a.description}</p>}
      </div>
      {a.date && (
        <div className="achievement-row__date">
          {new Date(a.date).toLocaleDateString("en-IN", { year: "numeric", month: "short" })}
        </div>
      )}
    </div>
  );
}

export function Achievements() {
  const { data, loading } = useFetch(getAchievements, []);
  const items = data || [];
  const { visibleItems, expanded, toggle, hasMore } = useCollapsible(items, COLLAPSE_COUNT);

  return (
    <section id="achievements" className="section event-section">
      <div className="container">
        <h2 className="section-title">
          <span className="section-title__index">05</span>
          Achievements
        </h2>
        {loading ? (
          <LoadingTerminal
            title="achievements.load"
            lines={[
              { variant: "headline" },
              { variant: "title" },
              { variant: "text" },
              { variant: "text", style: { width: "80%" } },
            ]}
            meta="loading achievements..."
          />
        ) : items.length === 0 ? (
          <div className="event-empty card">
            <h3 className="event-empty__title">No achievements yet</h3>
            <p className="event-empty__text">Add achievements in the admin panel to display them here.</p>
          </div>
        ) : (
          <>
            <div className="achievements-list">
              {visibleItems.map(a => <AchievementRow key={a.id} a={a} />)}
            </div>
            <ViewMoreButton expanded={expanded} toggle={toggle} total={items.length} initialCount={COLLAPSE_COUNT} />
          </>
        )}
      </div>
    </section>
  );
}
